/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';

import { ID, ProduceStatusDto } from '@gh/common-shared-data';
import {
  AbstractRestService, arrayOf,
  identity,
  none, optimisticLockingOf, privilegedTokenOf,
  RestSettings, SlicedDataSource,
} from '@gh/core-data';
import { parametrizeUrl } from '@gh/core-util';

import { Observable } from 'rxjs';
import {
  JobDrawCalculateRequestDto,
  JobDrawCalculateResponseDto,
  JobDrawCreateLendersScheduleRequestDto,
  JobDrawCreatePaymentScheduleRequestDto,
  JobDrawDetailsDto,
  JobDrawGeneratedDocsResponseDto,
  JobDrawPrintRequestDto,
  JobDrawsDto,
  JobDrawsRecalculateRequestDto,
} from './job-draws.dto';

import {
  JobDrawCreateLendersScheduleRequestMapper,
  JobDrawCreateRequestMapper,
  JobDrawDetailsMapper,
  JobDrawsMapper,
} from './job-draws.mapper';
import { JobListFilterDto, JobListItemDto } from '../job.dto';
import { JobListFilterMapper, JobListItemMapper } from '../job.mapper';

@Injectable()
export class JobDrawsRestService extends AbstractRestService {
  constructor(private settings: RestSettings) {
    super(settings);
  }

  getMaster(jobId: ID): Observable<JobDrawsDto> {
    return this.httpGet(`${this.bp}/jobs/${jobId}/draws`, JobDrawsMapper);
  }

  recalculateMaster(jobId: ID, request: JobDrawsRecalculateRequestDto, newDrawRuleId?: ID): Observable<JobDrawsDto> {
    return this.httpPut(
      parametrizeUrl(`${this.bp}/jobs/${jobId}/draws`, { drawRuleId: newDrawRuleId }),
      request,
      identity(), JobDrawsMapper);
  }

  refreshMaster(jobId: ID,
                lenderScheduleDrawRuleId: ID,
                request: JobDrawsRecalculateRequestDto): Observable<JobDrawsDto> {
    return this.httpPut(
      `${this.bp}/jobs/${jobId}/draws/${lenderScheduleDrawRuleId}/refresh`,
      request,
      identity(), JobDrawsMapper);
  }

  init(jobId: ID): Observable<JobDrawDetailsDto> {
    return this.httpGet(`${this.bp}/jobs/${jobId}/draws/init`, JobDrawDetailsMapper);
  }

  get(jobId: ID, invoiceId: ID): Observable<JobDrawDetailsDto> {
    return this.httpGet(`${this.bp}/jobs/${jobId}/draws/${invoiceId}`, JobDrawDetailsMapper);
  }

  createPaymentSchedule(jobId: ID, request: JobDrawCreatePaymentScheduleRequestDto): Observable<void> {
    return this.httpPost(
      `${this.bp}/jobs/${jobId}/draws/process`,
      request,
      privilegedTokenOf(optimisticLockingOf(JobDrawCreateRequestMapper)),
      none());
  }

  createLendersSchedule(jobId: ID, request: JobDrawCreateLendersScheduleRequestDto): Observable<void> {
    return this.httpPost(
      `${this.bp}/jobs/${jobId}/draws/process-lender-schedule`,
      request,
      JobDrawCreateLendersScheduleRequestMapper,
      none());
  }

  calculateGst(jobId: ID, request: JobDrawCalculateRequestDto): Observable<JobDrawCalculateResponseDto> {
    return this.httpPut(`${this.bp}/jobs/${jobId}/draws/calculate-gst`, request, identity(), identity());
  }

  print(jobId: ID, request: JobDrawPrintRequestDto): Observable<any> {
    return this.httpPost(`${this.bp}/jobs/${jobId}/draws/print`, request, identity(), identity());
  }

  getProduceStatus(jobId: ID, invoiceId?: ID): Observable<ProduceStatusDto> {
    return this.httpGet(`${this.bp}/jobs/${jobId}/draws/produce-status`, { invoiceId }, void 0, identity());
  }

  getGeneratedDocuments(jobId: ID, invoiceId?: ID): Observable<JobDrawGeneratedDocsResponseDto> {
    return this.httpGet(`${this.bp}/jobs/${jobId}/draws/docs`, { invoiceId }, void 0, identity());
  }

  produceDocs(jobId: ID, invoiceId: ID, preview: boolean): Observable<void> {
    return this.httpGet(`${this.bp}/jobs/${jobId}/draws/${invoiceId}/produce-docs`, { preview }, void 0, none());
  }

  findAllAsDataSource(filter: JobListFilterDto): SlicedDataSource<JobListItemDto> {
    return this.createSlicedDataSourceGet(`${this.jad}/claims`,
                                          filter, JobListFilterMapper, arrayOf(JobListItemMapper));
  }
}
